import { Link } from "@tanstack/react-router";
import { BookOpen, Eye, Heart } from "lucide-react";

import { authorLabel, type StoryCardData } from "@/lib/queries";
import { storyTags } from "@/lib/tags";
import { TagList } from "@/components/tag-chip";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

function initials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function StoryCard({
  story,
  featured = false,
}: {
  story: StoryCardData;
  featured?: boolean;
}) {
  const author = story.profiles;
  const tags = storyTags(story);

  return (
    <article
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-3xl border border-border/70 bg-gradient-card shadow-soft transition-smooth hover:-translate-y-1 hover:border-primary/50 hover:shadow-lift",
        featured && "md:flex-row",
      )}
    >
      <div
        className={cn(
          "relative overflow-hidden bg-accent/60",
          featured ? "md:w-2/5 aspect-[16/10] md:aspect-auto" : "aspect-[16/10]",
        )}
      >
        {story.cover_url ? (
          <img
            src={story.cover_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-smooth group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-hero-glow">
            <BookOpen className="h-8 w-8 text-primary-glow/70" aria-hidden="true" />
          </div>
        )}
        {story.content_types ? (
          <span className="absolute right-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground backdrop-blur">
            {story.content_types.name}
          </span>
        ) : null}
        {story.categories ? (
          <span className="absolute left-4 top-4 rounded-full bg-background/80 px-3 py-1 text-xs font-medium text-foreground backdrop-blur">
            {story.categories.name}
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3
          className={cn(
            "font-display leading-snug text-foreground",
            featured ? "text-2xl" : "text-lg",
          )}
        >
          <Link
            to="/story/$storyId"
            params={{ storyId: story.id }}
            className="after:absolute after:inset-0 after:content-['']"
          >
            {story.title}
          </Link>
        </h3>

        {story.description ? (
          <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
            {story.description}
          </p>
        ) : null}

        <TagList tags={tags} max={3} />

        <div className="mt-auto flex items-center justify-between gap-3 pt-2">
          {author?.username ? (
            <Link
              to="/u/$username"
              params={{ username: author.username }}
              className="relative z-10 flex min-w-0 items-center gap-2 transition-smooth hover:text-foreground"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent text-[10px] font-semibold text-accent-foreground">
                {author.avatar_url ? (
                  <img
                    src={author.avatar_url}
                    alt={`${authorLabel(story)} profile photo`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initials(authorLabel(story))
                )}
              </span>
              <span className="truncate text-xs text-muted-foreground">{authorLabel(story)}</span>
            </Link>
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent text-[10px] font-semibold text-accent-foreground">
                {initials(authorLabel(story))}
              </span>
              <span className="truncate text-xs text-muted-foreground">{authorLabel(story)}</span>
            </div>
          )}

          <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Heart className="h-3.5 w-3.5" aria-hidden="true" />
              {story.like_count}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              {story.view_count}
            </span>
            <span>{story.reading_minutes || 1} min</span>
          </div>
        </div>
      </div>
    </article>
  );
}

export function StoryCardSkeleton({ featured = false }: { featured?: boolean }) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-3xl border border-border/60 bg-surface/50",
        featured && "md:flex",
      )}
    >
      <Skeleton className={cn("aspect-[16/10] w-full", featured && "md:w-2/5")} />
      <div className="flex-1 space-y-3 p-5">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}
